// @ts-strict-ignore
import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { getVersion } from '@fc-shared/utils/data.utils';
import { NgButtonComponent } from '@fc-shared/ui/buttons/ng-button';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { HasFeatureModule } from '@fc-shared/directives/has-feature/has-feature.module';
import { AvatarModule } from '@fc-shared/ui/avatar/avatar.module';
import {
  currentUserHasLegacyPermission,
  getCurrentEmployee,
  getCurrentUser,
  getEmployeeById,
} from '@fc-store/selectors';
import { AuthService, DialogsService } from '@fc-core';
import { PermissionsService } from '@fc-core/services/permissions.service';
import { PwaService } from '@fc-core/services/pwa-service';
import { CopyToClipDirective } from '@fc-shared/directives/copy-to-clip.directive';
import { AmplitudeService } from '@fc-core/services/amplitude.service';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { HeaderFacade } from './header.facade';
import { HelpCenterDialogComponent } from '@fc-core/components/header/help-center-dialog/help-center-dialog.component';
import { getShowNews } from '@fc-store/selectors/news.selectors';

@Component({
  selector: 'fc-employee-menu',
  imports: [
    RouterModule,
    NgButtonComponent,
    IconComponent,
    HasFeatureModule,
    AvatarModule,
    CopyToClipDirective,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      [matMenuTriggerFor]="menu"
      class="flex cursor-pointer"
      [class.is-hijacked]="isHijacked"
      (click)="triggerAnalytics('topbar_click_user_dropdown')"
    >
      <fc-entity-avatar
        type="employee"
        [size]="36"
        [entity]="currentEmployee()"
      ></fc-entity-avatar>

      @if (isHijacked && currentUser()) {
        <div class="-ml-[10px] border-2 border-sky-200 rounded-full">
          <fc-avatar-placeholder
            [color]="'#FFC107'"
            [fontSize]="12"
            [size]="36"
            [text]="currentUser().email[0]"
            shape="circle"
          ></fc-avatar-placeholder>
        </div>
      }
    </div>

    <mat-menu #menu="matMenu">
      @if (isHijacked && currentUser()) {
        <span
          class="w-full flex items-center gap-12 py-12 px-16 text-grey-300 text-sm"
        >
          {{ currentUser().email }} user
        </span>
      }
      <!-- My account -->
      <button
        [routerLink]="['/settings/account']"
        mat-menu-item
        (click)="triggerAnalytics('user_dropdown_click_account')"
      >
        <fc-entity-avatar
          type="employee"
          [size]="20"
          [entity]="currentEmployee()"
        />
        <span class="text-sm text-grey-900"> My Account </span>
      </button>
      <div class="flex flex-row px-8 py-4 w-full">
        <ng-button
          class="w-full"
          variant="outline"
          size="sm"
          (click)="openStatusSetDialog()"
          color="secondary"
          icon="icon-emoji"
        >
          @if (!currentEmployee()?.setstatus?.status) {}
          {{ statusText }}
        </ng-button>
      </div>

      <div class="block sm:hidden">
        <button (click)="openActivity()" mat-menu-item>
          <div class="w-full flex items-center gap-12">
            <fc-icon [size]="20" icon="icon-notification-outline" />
            Activity
          </div>
        </button>
      </div>

      <div class="block sm:hidden">
        <button (click)="openMobileHelp()" mat-menu-item>
          <div class="w-full flex items-center gap-12">
            <fc-icon [size]="20" icon="icon-help-outline" />
            Help center
          </div>
        </button>
      </div>

      <div class="block sm:hidden">
        <button (click)="openNews()" mat-menu-item>
          <div class="w-full flex items-center gap-12">
            <fc-icon [size]="20" icon="icon-news-outline" />
            <span>News</span>
            @if (unreadNewsCount() > 0) {
              <div
                class="ml-auto inline-flex min-w-16 h-16 items-center justify-center rounded-full bg-primary-500 px-4 text-xs font-medium text-white"
              >
                {{ unreadNewsCount() < 99 ? unreadNewsCount() : '99+' }}
              </div>
            }
          </div>
        </button>
      </div>

      <!-- Add to screen -->
      @if (permissions.havePermissions) {
        <button
          (click)="pwaService.initAppToScreen()"
          mat-menu-item
          [class.disabled]="!pwaService.canInstall()"
          [disabled]="!pwaService.canInstall()"
        >
          <div class="w-full flex items-center gap-12">
            <fc-icon [size]="20" icon="icon-download" />
            <span class="text-sm text-grey-900"> Add To Home Screen </span>
          </div>
        </button>
      }
      @if (haveDevOptions()) {
        <button (click)="openDeveloperOptions()" mat-menu-item>
          <div class="w-full flex items-center gap-12">
            <fc-icon [size]="20" icon="icon-scroll" />
            <span class="text-sm text-grey-900"> Developer Options </span>
          </div>
        </button>
      }
      @if (canSwitchCustomer()) {
        <button (click)="openChangeCustomer()" mat-menu-item>
          <div class="w-full flex items-center gap-12">
            <fc-icon [size]="20" icon="icon-business" />
            <span class="text-sm text-grey-900"> Change Customer </span>
          </div>
        </button>
      }

      <hr class="border-t border-grey-200 w-full" />
      <button [text]="version" mat-menu-item fcCopyToClip>
        <div class="w-full flex items-center gap-12">
          <fc-icon [size]="20" icon="icon-copy" />
          <div class="flex flex-1 flex-row justify-between">
            <div class="text-sm text-grey-900">Version:</div>
            <div class="text-xs text-secondary-400">{{ version }}</div>
          </div>
        </div>
      </button>
      <hr class="border-t border-grey-200 w-full" />
      <button (click)="openConfirmLogout()" mat-menu-item>
        <div class="w-full flex items-center gap-12">
          <fc-icon [size]="20" icon="icon-logout" />
          <span class="text-sm text-grey-900"> Logout </span>
        </div>
      </button>
    </mat-menu>
  `,
})
export class EmployeeMenuComponent {
  store = inject(Store);
  amplitude = inject(AmplitudeService);
  router = inject(Router);
  dialogs = inject(DialogsService);
  permissions = inject(PermissionsService);
  pwaService = inject(PwaService);
  authService = inject(AuthService);
  headerFacade = inject(HeaderFacade);

  isHijacked = this.authService.tokenData?.is_hijacked;
  currentEmployee = computed(() => {
    const currentEmployee = this.store.selectSignal(getCurrentEmployee)();
    return this.store.selectSignal(getEmployeeById(currentEmployee?.id))();
  });
  currentUser = this.store.selectSignal(getCurrentUser);
  unreadNewsCount = this.store.selectSignal(getShowNews);
  canSwitchCustomer = this.store.selectSignal(
    currentUserHasLegacyPermission(
      'accounting_customer_can_switch_customer_view',
    ),
  );
  haveDevOptions = computed(() => this.currentUser()?.isStaff);

  openNews() {
    this.amplitude.track('topbar_click_news');
    this.headerFacade.showNews.set(true);
    this.headerFacade.showEvents.set(false);
  }

  openActivity() {
    this.amplitude.track('topbar_click_activity');
    this.headerFacade.showNews.set(false);
    this.headerFacade.showEvents.set(true);
  }

  get version(): string {
    return getVersion();
  }

  get statusText(): string {
    return this.headerFacade.getStatusText(this.currentEmployee());
  }

  triggerAnalytics(event: string): void {
    this.amplitude.track(event);
  }

  openDeveloperOptions() {
    this.headerFacade.openDeveloperOptions();
  }

  openConfirmLogout() {
    this.headerFacade.openConfirmLogout();
  }

  openMobileHelp() {
    this.amplitude.track('topbar_click_help');
    this.dialogs.openMobileDialog(HelpCenterDialogComponent);
  }

  openChangeCustomer() {
    if (this.canSwitchCustomer()) {
      this.headerFacade.openChangeCustomer();
    }
  }

  openStatusSetDialog(): void {
    this.headerFacade.openStatusSetDialog(
      this.currentEmployee()?.id,
      this.currentEmployee()?.setstatus,
    );
  }
}
